import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../core/providers/AuthProvider';
import { Icon } from '../../../../core/design-system/components/Icon';
import { Spinner } from '../../../../core/design-system/components/Spinner';
import Avatar from '../../../../core/design-system/components/Avatar';
import { formatMonthYear } from '../../../../shared/utils/date/DateFormatters';
import { useProfilePhoto } from '../../hooks/useProfilePhoto';
import { getLevelText, getLevelIcon, getInterestText, getInterestIcon } from '../../../../core/utils/translations';
import './styles.css';

const ProfileHeader = ({ 
  user, 
  onProfileUpdate, 
  restrictedView, 
  isFollowing, 
  isPending, 
  isLoading,
  onFollow, 
  onUnfollow, 
  onCancelRequest,
  onViewFollowing,
  onViewFollowers,
  onViewRequests,
  isOwnProfile
}) => {
  const { t } = useTranslation('profile');
  const { t: tCommon } = useTranslation('common');
  const { user: currentUser } = useAuth();
  const { uploadPhoto } = useProfilePhoto();
  const [currentPhotoURL, setCurrentPhotoURL] = useState(user?.photoURL);
  const [isUploading, setIsUploading] = useState(false);
  
  const ownProfile = isOwnProfile !== undefined ? isOwnProfile : currentUser?.uid === user?.uid;
  const formattedJoinDate = formatMonthYear(user?.joinDate);

  // Helper pour formater l'adresse
  const getFormattedLocation = () => {
    if (!user?.location) return null;
    return typeof user.location === 'object' ? user.location.address : user.location;
  };

  const handlePhotoChange = async (file) => {
    if (!file) {
      return;
    }
    
    // Validations
    if (!file.type.startsWith('image/')) {
      console.error('Le fichier sélectionné n\'est pas une image');
      return;
    }
    
    // Limite de taille (5MB)
    if (file.size > 5 * 1024 * 1024) {
      console.error('L\'image ne doit pas dépasser 5MB');
      return;
    }
    
    setIsUploading(true);
    
    try {
      // Afficher une prévisualisation avant l'upload
      const reader = new FileReader();
      reader.onload = (e) => {
        setCurrentPhotoURL(e.target.result);
      };
      reader.readAsDataURL(file);
      
      // Upload la photo
      const url = await uploadPhoto(file);
      
      // Mettre à jour le state local avec l'URL définitive
      if (url) {
        setCurrentPhotoURL(url);
        if (onProfileUpdate) {
          onProfileUpdate();
        }
      }
    } catch (error) {
      console.error("Erreur lors du changement de photo:", error);
    } finally {
      setIsUploading(false);
    }
  };

  // Afficher un placeholder ou l'avatar de l'utilisateur
  const renderAvatar = () => {
    if (isUploading) {
      return (
        <div className="profile-avatar-loading">
          <Spinner size="medium" />
        </div>
      );
    } else {
      return (
        <Avatar 
          text={user?.displayName?.charAt(0)?.toUpperCase() || '?'}
          src={currentPhotoURL}
          size="xl"
          className="profile-avatar"
        />
      );
    }
  };

  if (restrictedView) {
    // En mode restreint, on n'affiche que:
    // - Photo de profil
    // - Pseudo/nom
    // - Bouton suivre (géré dans ProfilePage)
    return (
      <div className="profile-header">
        <div className="profile-avatar-container">
          {renderAvatar()}
        </div>
        <div className="profile-name-level">
          <h1 className="profile-name">{user.displayName}</h1>
          <span className="profile-level">{user.level || "Débutant"}</span>
        </div>
        {/* Rien d'autre n'est affiché en mode restreint */}
      </div>
    );
  }

  return (
    <div className="profile-header">
      <div className="profile-avatar-container">
        {renderAvatar()}
        {ownProfile && (
          <>
            <input 
              type="file"
              onChange={(e) => handlePhotoChange(e.target.files[0])}
              accept="image/*"
              id="profile-photo-input"
              style={{ display: 'none' }}
            />
            <label 
              htmlFor="profile-photo-input" 
              className="edit-avatar-button"
            >
              <Icon name="edit" size={14} />
              <div className="plus-badge">+</div>
            </label>
          </>
        )}
      </div>

      <div className="profile-info-centered">
        <div className="name-level-container">
          <h1 className="profile-name">{user?.displayName || t('default.displayName') || ''}</h1>
          <span className="level-badge-inline">
            <Icon name={getLevelIcon(user?.badges)} size={14} />
            {getLevelText(user?.badges, tCommon)}
          </span>
        </div>

        {getFormattedLocation() && (
          <div className="profile-location">
            <Icon name="map-pin" size={14} />
            <span>{getFormattedLocation()}</span>
          </div>
        )}

        <div className="profile-joined">
          <span>{t('header.joinDate', { date: formattedJoinDate || t('header.unknownJoinDate') })}</span>
        </div>

        {user?.bio && (
          <div className="profile-bio">
            <p>{user.bio}</p>
          </div>
        )}

        <div className="profile-stats-container">
          <div className="stats-row">
            <div 
              className="stat-column clickable" 
              onClick={() => onViewFollowing && onViewFollowing()}
            >
              <span className="stat-number">{user?.following?.length || 0}</span>
              <span className="stat-label">{t('stats.following') || 'abonnements'}</span>
            </div>
            <div 
              className="stat-column clickable" 
              onClick={() => onViewFollowers && onViewFollowers()}
            >
              <span className="stat-number">{user?.followers?.length || 0}</span>
              <span className="stat-label">{t('stats.followers') || 'abonnés'}</span>
              {/* Badge pour les demandes en attente */}
              {user?.followRequests?.length > 0 && ownProfile && (
                <span 
                  className="notification-badge" 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onViewRequests) onViewRequests();
                  }}
                >
                  {user.followRequests.length}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="interests-row">
          {user?.interests?.slice(0, 3).map((interest, index) => {
            const icon = getInterestIcon(interest, tCommon);
            
            return (
              <div key={`interest-${index}`} className="interest-badge-inline">
                <span className="interest-emoji">{icon}</span>
                <span>{getInterestText(interest, tCommon)}</span>
              </div>
            );
          })}
          
          {(!user?.interests || user?.interests.length === 0) && (
            <div className="interest-badge-inline">
              <span className="interest-emoji">❓</span>
              <span>{tCommon('interests.sport')}</span>
            </div>
          )}

          <div className="credits-inline">
            <span>{user?.credits || 0} crédits</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader; 